"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAirQualityData = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
const DEFAULT_DAYS = 30;
/**
 * Transforms SensorUsageRollup data into DailyAirQualityData format
 */
const transformAirQualityData = (airQualityData) => airQualityData.map((dailyData) => {
    // Get air quality data from the rollup object
    const airQualityRollup = dailyData.airQualityRollup;
    return {
        label: dailyData.date,
        temperature: airQualityRollup?.avgTemperature || 0,
        humidity: airQualityRollup?.avgHumidity || 0,
        iaq: airQualityRollup?.avgIAQ || 0,
        pm: airQualityRollup?.avgPM || 0,
        pressure: airQualityRollup?.avgPressure || 0,
    };
});
/**
 * Hook that provides air quality data based on SensorUsageRollup records
 * @param params - Parameters for fetching air quality data
 * @returns Air quality data with daily metrics
 */
/**
 * Converts a SensorEvent to SingleAirQualityData format
 * @param event - The sensor event to convert
 * @returns SingleAirQualityData object or undefined if conversion fails
 */
const eventToSingleAirQualityData = (event) => {
    if (!event)
        return undefined;
    try {
        // Try to get values from the event
        let values;
        if (typeof event.values === 'object') {
            values = event.values;
        }
        else if (typeof event.values === 'string') {
            try {
                values = JSON.parse(event.values);
            }
            catch (e) {
                console.error('Error parsing event.values string:', e);
            }
        }
        // Try to get values from rawData if not in values
        if (!values && event.rawData) {
            try {
                values = JSON.parse(event.rawData);
            }
            catch (e) {
                console.error('Error parsing event.rawData:', e);
            }
        }
        if (!values) {
            console.log(`No values found in event ${event.id}`);
            return undefined;
        }
        console.log(`Converting event ${event.id} to SingleAirQualityData:`, values);
        // Create a date string from the event timestamp
        const timestamp = event.timestamp ? new Date(event.timestamp * 1000) : new Date();
        const dateString = timestamp.toISOString();
        // Extract values safely, handling both direct property access and parsed values
        const temperature = values.temperature ?? values.avgTemperature ?? 0;
        const humidity = values.humidity ?? values.avgHumidity ?? 0;
        const iaq = values.iaq ?? 0;
        const pm = values.pm ?? values.avgPM ?? 0;
        const pressure = values.pressure ?? values.avgPressure ?? 0;
        return {
            label: dateString,
            temperature,
            humidity,
            iaq,
            pm,
            pressure,
        };
    }
    catch (err) {
        console.error('Error converting sensor event to air quality data:', err);
        return undefined;
    }
};
const useAirQualityData = ({ sensorId, days = DEFAULT_DAYS, enabled = true, }) => {
    const [data, setData] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const [latestData, setLatestData] = (0, react_1.useState)(undefined);
    // Calculate date range
    const endDate = (0, react_1.useMemo)(() => new Date().toISOString().split('T')[0], // Today in YYYY-MM-DD
    []);
    const startDate = (0, react_1.useMemo)(() => {
        const date = new Date();
        date.setDate(date.getDate() - days);
        return date.toISOString().split('T')[0]; // N days ago in YYYY-MM-DD
    }, [days]);
    /**
     * Uses the useSensorEvents hook functionality to fetch the latest valid sensor event
     * with iaq and humidity data
     * @param sensorId - The ID of the sensor to fetch events for
     * @returns The latest valid sensor event or undefined
     */
    const fetchLatestSensorEvent = async (sensorId) => {
        try {
            console.log(`Fetching latest sensor events for sensor ${sensorId}`);
            // Use API.graphql directly to get the most recent events
            const result = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorEventsBySensor,
                variables: {
                    sensorId,
                    limit: 10, // Get several recent events to find one with valid data
                },
            });
            // Extract the sensor events from the result
            const events = result?.data?.getSensorEventsBySensor || [];
            console.log(`Found ${events.length} sensor events`);
            // Process each event to find one with valid iaq and humidity values
            for (const event of events) {
                let values = null;
                // Try to parse values from the event
                if (typeof event.values === 'object') {
                    values = event.values;
                }
                else if (typeof event.values === 'string') {
                    try {
                        values = JSON.parse(event.values);
                    }
                    catch (_e) {
                        // Try the next event if parsing fails
                        continue;
                    }
                }
                else if (event.rawData) {
                    try {
                        values = JSON.parse(event.rawData);
                    }
                    catch (_e) {
                        // Try the next event if parsing fails
                        continue;
                    }
                }
                console.log(`Processing event ${event.id}:`, values);
                // If we have values, check if they include iaq and humidity
                if (values) {
                    const hasIAQ = values.iaq !== undefined;
                    const hasHumidity = values.humidity !== undefined || values.avgHumidity !== undefined;
                    console.log(`Event ${event.id} checks - hasIAQ: ${hasIAQ}, hasHumidity: ${hasHumidity}`);
                    // If both conditions are met, use this event
                    if (hasIAQ && hasHumidity) {
                        console.log(`Found valid event with iaq=${values.iaq}, humidity=${values.humidity || values.avgHumidity}`);
                        return event;
                    }
                }
            }
            console.log('No sensor events with valid IAQ and humidity found');
            // Return the first event as a fallback
            return events.length > 0 ? events[0] : undefined;
        }
        catch (error) {
            console.error('Error fetching latest air quality sensor event:', error);
            return undefined;
        }
    };
    const fetchAirQualityData = async () => {
        if (!enabled || !sensorId) {
            setLatestData(undefined);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // Fetch historical rollup data
            const result = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorUsageRollup,
                variables: {
                    sensorId,
                    startDate,
                    endDate,
                    nextToken: undefined, // For now, we'll handle pagination later if needed
                },
            });
            // Fetch the latest sensor event with valid data
            const latestEvent = await fetchLatestSensorEvent(sensorId);
            // Convert the sensor event to SingleAirQualityData and update state
            if (latestEvent) {
                const singleData = eventToSingleAirQualityData(latestEvent);
                setLatestData(singleData);
            }
            else {
                setLatestData(undefined);
            }
            const rawData = result?.data?.getSensorUsageRollup;
            const sensorUsageData = rawData?.items || rawData || [];
            if (sensorUsageData && Array.isArray(sensorUsageData) && sensorUsageData.length > 0) {
                const historicalData = transformAirQualityData(sensorUsageData);
                setData(historicalData);
            }
            else {
                setData([]);
            }
        }
        catch (err) {
            console.error('Error fetching air quality data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch air quality data');
            setData(null);
            setLatestData(undefined);
        }
        finally {
            setLoading(false);
        }
    };
    const refetch = async () => {
        await fetchAirQualityData();
    };
    (0, react_1.useEffect)(() => {
        fetchAirQualityData();
    }, [sensorId, startDate, endDate, enabled]);
    return {
        data,
        loading,
        error,
        refetch,
        latestData,
    };
};
exports.useAirQualityData = useAirQualityData;
